/**
 * Issue #224 — 审计日志（登录日志 / 操作日志）列表的列隐藏 + 分页 + 查询构建（纯函数）。
 *
 * 复用员工管理的「列隐藏 + 默认 20/页分页 + 高级搜索」语义。日志由后端
 * GET /admin/audit-logs 服务端分页 + 检索（org 隔离在后端强制），前端只持有筛选状态、
 * 列可见性，并把查询参数拼给接口——绝不前端假状态。
 */

export interface AuditColumnMeta {
  key: string;
  label: string;
  hideable: boolean;
  defaultVisible: boolean;
}

/** 审计日志列白名单（顺序即展示顺序）。createdAt（时间）不可隐藏，恒可见。 */
export const AUDIT_COLUMNS: readonly AuditColumnMeta[] = [
  { key: 'createdAt', label: '时间', hideable: false, defaultVisible: true },
  { key: 'actionLabel', label: '事件', hideable: true, defaultVisible: true },
  { key: 'operatorName', label: '操作者', hideable: true, defaultVisible: true },
  { key: 'summary', label: '摘要', hideable: true, defaultVisible: true },
  { key: 'resourceType', label: '资源类型', hideable: true, defaultVisible: false },
  { key: 'resourceId', label: '资源ID', hideable: true, defaultVisible: false },
  { key: 'ipAddress', label: 'IP', hideable: true, defaultVisible: false },
] as const;

export const AUDIT_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
export const AUDIT_DEFAULT_PAGE_SIZE = 20;

export interface AuditFilterState {
  action: string; // '' = 全部
  keyword: string;
  startDate: string;
  endDate: string;
  page: number;
  pageSize: number;
  /** Issue #229：是否包含归档线（2026-06-12 23:40）之前的历史日志。默认 false。 */
  includeArchived: boolean;
}

export const DEFAULT_AUDIT_FILTER: AuditFilterState = {
  action: '',
  keyword: '',
  startDate: '',
  endDate: '',
  page: 1,
  pageSize: AUDIT_DEFAULT_PAGE_SIZE,
  includeArchived: false,
};

/** 可见列：默认集来自 AUDIT_COLUMNS.defaultVisible；强制含不可隐藏列，按白名单顺序。 */
export function resolveAuditColumns(preferred: string[] | null | undefined): string[] {
  const order = AUDIT_COLUMNS.map((c) => c.key);
  const whitelist = new Set(order);
  const nonHideable = new Set(AUDIT_COLUMNS.filter((c) => !c.hideable).map((c) => c.key));
  const base =
    preferred && preferred.length > 0
      ? preferred
      : AUDIT_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);
  const baseSet = new Set(base.filter((k) => whitelist.has(k)));
  return order.filter((k) => baseSet.has(k) || nonHideable.has(k));
}

/** 切换列显隐：仅白名单内且可隐藏的列可切换；其余原样返回。 */
export function toggleAuditColumn(current: string[], key: string): string[] {
  const meta = AUDIT_COLUMNS.find((c) => c.key === key);
  if (!meta || !meta.hideable) return current;
  if (current.includes(key)) return current.filter((c) => c !== key);
  const order = AUDIT_COLUMNS.map((c) => c.key);
  return order.filter((c) => current.includes(c) || c === key);
}

export function normalizeAuditPageSize(value: number): number {
  return (AUDIT_PAGE_SIZE_OPTIONS as readonly number[]).includes(value)
    ? value
    : AUDIT_DEFAULT_PAGE_SIZE;
}

/** 把筛选状态拼成后端查询参数（空值省略，绝不发空字符串污染过滤）。 */
export function buildAuditQuery(state: AuditFilterState): Record<string, unknown> {
  const q: Record<string, unknown> = {
    page: Math.max(1, Math.floor(state.page)),
    limit: normalizeAuditPageSize(state.pageSize),
  };
  if (state.action.trim()) q.action = state.action.trim();
  if (state.keyword.trim()) q.keyword = state.keyword.trim();
  if (state.startDate.trim()) q.startDate = state.startDate.trim();
  if (state.endDate.trim()) q.endDate = state.endDate.trim();
  // Issue #229：默认软隐藏归档日志，仅在显式勾选时下发 includeArchived。
  if (state.includeArchived) q.includeArchived = 'true';
  return q;
}
